import './style.css'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import Stats from 'stats.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';

// Add imports for the classes moved to dedicated files
import LoadingManager from './LoadingManager.js'
import SceneManager from './SceneManager.js'
import CameraController from './CameraController.js'
import NOISE_GLSL from './noiseGLSL.js'
import AssetLoader from './AssetLoader.js'
import PlaneManager from './PlaneManager.js'
import Cars from './Cars.js'

import { HeightFogShader, BloomTintShader,hollowgramShader,crtPosterShaderStable,crtPosterShaderMoving} from './shaders.js'

//=============================================================================
// CONFIGURATION
//=============================================================================

// const fogColor = new THREE.Color(0x060b1a); // very dark blue
// const fogColor = new THREE.Color(0x101733); // indigo 0xfff
const fogColor = new THREE.Color(0x1b2240); // bluish purple
// const bgColor = new THREE.Color(0xfff); // bluish purple
const bgColor = new THREE.Color(0x101733); // bluish purple


const CONFIG = {
  isMobile: /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
  enableStats: true,
  enableGui: false,
  enablePostProcessing: true,
  antialias: true,
  shadows: true,
  pixelRatio: window.devicePixelRatio
}

//=============================================================================
// MAIN APPLICATION
//=============================================================================

class App {
  constructor() {
    this.loadingManager = new LoadingManager()
    this.assetLoader = new AssetLoader()
    this.sceneManager = new SceneManager()
    this.buildings = []
    this.crtMaterial = null;
    this.movingCrtMaterial = null;
    this.planeManager = new PlaneManager();
    this.planeMaterial = [];
    this.totalTime = 0.0;

    // Fish
    this.fish = null;
    this.fishTime = 0;
    this.fishCenter = new THREE.Vector3(43, 11, 9); // center point for fish movement
    this.fishInitialPositions = [];
    this.fishSpeeds = [];
    this.fishRadii = [];
    this.fishOffsets = [];
    this.fishPrevRotations = [];

    // Flying cars
    this.carPaths = [
      {
        start: new THREE.Vector3(-10, 25, -30),
        end: new THREE.Vector3(5, 25, 20),
        count: 25
      },
      {
        start: new THREE.Vector3(35, 15, -25),
        end: new THREE.Vector3(80, 17, 20),
        count: 60
      },
    ];
    
    // Optional: Custom camera controller
    this.cameraController = new CameraController(this.sceneManager.camera)
    
    this.init()
  }

  async makeBackgroundInstances(bgBuildingMeshes) {

    const cityCenter = new THREE.Vector3(19, 5.13, -6); // where your main city is
    const cityRadius = 42;      // approx radius of your main building area
    const margin = 0;          // gap between main city and bg
    const outerRadius = 100;    // how far bg buildings go

    const innerRadius = cityRadius + margin;
    
    bgBuildingMeshes.forEach(async (mesh, index) => {
      let count = 10 + Math.floor(Math.random() * 20); // 10–30 instances per type
      // count = 1;
      // console.log(`Creating ${count} instances for mesh ${index}: ${mesh.name}`);

      await this.createInstancedBuildingsForMesh({
        sourceMesh: mesh,
        count,
        innerRadius,
        outerRadius,
        center: cityCenter,
        minScaleY: 0.8,
        maxScaleY: 1.5,
      });
      
      // Hide original mesh, since we only want instances
      mesh.visible = false;
    });
  }
  
  async createInstancedBuildingsForMesh({
    sourceMesh,
    count,
    innerRadius,
    outerRadius,
    center = new THREE.Vector3(0, 0, 0),
    minScaleY = 0.7,
    maxScaleY = 3.0,
  }) {
    const geometry = sourceMesh.geometry;
    const material = sourceMesh.material.clone(); // clone so you can tweak color if needed

    const instanced = new THREE.InstancedMesh(geometry, material, count);
    const dummy = new THREE.Object3D();

    for (let i = 0; i < count; i++) {
      const pos = await this.randomPointOutsideCircle(innerRadius, outerRadius, center);
      // console.log(`Instance ${i}, ${pos} position: ${pos.x}, ${pos.y}, ${pos.z}`);

      const rotY = Math.random() * Math.PI * 2;

      const sX = THREE.MathUtils.lerp(0.8, 1.3, Math.random());
      const sZ = THREE.MathUtils.lerp(0.8, 1.3, Math.random());
      const sY = THREE.MathUtils.lerp(minScaleY, maxScaleY, Math.random());

      dummy.position.copy(pos);
      dummy.rotation.set(0, rotY, 0);
      dummy.scale.set(sX, sY, sZ);
      dummy.updateMatrix();

      instanced.setMatrixAt(i, dummy.matrix);
    }

    instanced.instanceMatrix.needsUpdate = true;
    instanced.frustumCulled = true;

    // Make them dark silhouettes
    if (instanced.material.isMeshStandardMaterial) {
      instanced.material.color.set(0x11141f);   // dark blue/grey
      instanced.material.emissive.set(0x000000);
      instanced.material.roughness = 1.0;
      instanced.material.metalness = 0.0;
    }

    this.sceneManager.scene.add(instanced);
    return instanced;
  }

  async randomPointOutsideCircle(innerRadius, outerRadius, center = new THREE.Vector3(0, 0, 0)) {
    const angle = Math.random() * Math.PI * 2;
    const radius = THREE.MathUtils.lerp(innerRadius, outerRadius, Math.random());
    // console.log(`Angle: ${angle}, Radius: ${radius}`);

    const x = Math.cos(angle) * radius + center.x;
    const z = Math.sin(angle) * radius + center.z;
    const y = center.y; // ground level

    return new THREE.Vector3(x,0,z);
  }

  randomPointInCircle(center, radius) {
    const angle = Math.random() * Math.PI * 2;
    const r = Math.random() * radius;
    const x = Math.cos(angle) * r + center.x;
    const z = Math.sin(angle) * r + center.z;
    const y = center.y + ((Math.random() - 0.5) * 10); // small vertical variation
    return new THREE.Vector3(x, y, z);
  }


  async randomBuildingLocations() {
    //generate random building locations and choose random buildings from the array place them in the scene
    for (let i = 0; i < 50; i++) {
      const building = this.buildings[Math.floor(Math.random() * this.buildings.length)].geometry.clone();
      building.position.set(Math.random() * 2 * 50 - 50, 0, Math.random() * 2 * 50 - 50);
      this.sceneManager.scene.add(building);
    }
  }

  
  initFish(fishCnt, fishMesh, center) {
    let geometry = fishMesh.geometry;
    // ❌ REMOVE MORPH TARGETS COMPLETELY
    geometry.morphAttributes = {};
    geometry.morphTargetsRelative = false;

    // ❌ REMOVE MORPH FLAGS
    geometry.userData.morphTargets = false;

    // (Optional but safe) force non-indexed
    if (geometry.index) {
      geometry = geometry.toNonIndexed();
    }

    fishMesh.material.instancing = true;
    const material = fishMesh.material;
    const instanced = new THREE.InstancedMesh(geometry, material, fishCnt);
    const dummy = new THREE.Object3D();
    const radius = 3; // radius around the center point

    // Initialize arrays for fish movement
    this.fishInitialPositions = [];
    this.fishSpeeds = [];
    this.fishRadii = [];
    this.fishOffsets = [];
    this.fishPrevRotations = [];

    for (let i = 0; i < fishCnt; i++) {
      const pos = this.randomPointInCircle(center, radius);
      
      const rotY = Math.random() * Math.PI * 2;
      dummy.position.copy(pos);
      dummy.rotation.set(0, rotY, 0);
      dummy.updateMatrix();
      instanced.setMatrixAt(i, dummy.matrix);

      // Store initial data for movement
      this.fishInitialPositions.push(pos.clone());
      this.fishSpeeds.push(0.3); // speed between 0.5 and 1.5
      this.fishRadii.push(radius + Math.random() * 2); // radius between 2 and 7
      this.fishOffsets.push(Math.random() * Math.PI * 2); // random phase offset
      this.fishPrevRotations.push(rotY);
    }

    instanced.instanceMatrix.needsUpdate = true;
    this.fish = instanced;
    this.sceneManager.scene.add(instanced);
  }

  lerpAngle(a, b, t) {
    let delta = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI;
    return a + delta * t;
  }

  updateFish(delta) {
    if (!this.fish) return;

    this.fishTime += delta;

    const dummy = new THREE.Object3D();

    for (let i = 0; i < this.fish.count; i++) {
      const speed  = this.fishSpeeds[i];
      const radius = this.fishRadii[i];
      const offset = this.fishOffsets[i];

      const t = this.fishTime * speed + offset;

      // Orbit around ONE shared center
      const x = this.fishCenter.x + Math.cos(t) * radius;
      const z = this.fishCenter.z + Math.sin(t) * radius;
      const y = this.fishCenter.y + Math.sin(t * 2.0) * 0.3;

      const currentPos = new THREE.Vector3(x, y, z);

      // Previous position (velocity approximation)
      const prevT = (this.fishTime - delta) * speed + offset;
      const prevPos = new THREE.Vector3(
        this.fishCenter.x + Math.cos(prevT) * radius,
        this.fishCenter.y + Math.sin(prevT * 2.0) * 0.3,
        this.fishCenter.z + Math.sin(prevT) * radius
      );

      // Direction of movement
      const dir = currentPos.clone().sub(prevPos).normalize();

      // Face direction of motion (Y-axis)
      const targetRotY = Math.atan2(dir.x, dir.z);

      // Smooth turning (correct + stable)
      const prevRotY = this.fishPrevRotations[i];
      const rotY = this.lerpAngle(prevRotY, targetRotY, 0.12);
      this.fishPrevRotations[i] = rotY;

      // Optional natural body sway
      const sway = Math.sin(t * 3.0) * 0.12;

      dummy.position.copy(currentPos);
      dummy.rotation.set(0, rotY + sway, 0);
      dummy.rotation.y = rotY - Math.PI * 0.5;
      dummy.scale.setScalar(1);
      dummy.updateMatrix();

      this.fish.setMatrixAt(i, dummy.matrix);
    }

    this.fish.instanceMatrix.needsUpdate = true;
  }

  createPointyStarGeometry() {
    const geometry = new THREE.BufferGeometry();

    const vertices = new Float32Array([
      // ---- Vertical spike (up) ----
      0.0,  1.0, 0.0,
      -0.15, 0.0, 0.0,
      0.15, 0.0, 0.0,

      // ---- Vertical spike (down) ----
      0.0, -1.0, 0.0,
      0.15,  0.0, 0.0,
      -0.15,  0.0, 0.0,

      // ---- Horizontal spike (right) ----
      1.0,  0.0, 0.0,
      0.0,  0.15, 0.0,
      0.0, -0.15, 0.0,

      // ---- Horizontal spike (left) ----
      -1.0,  0.0, 0.0,
      0.0, -0.15, 0.0,
      0.0,  0.15, 0.0
    ]);

    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(vertices, 3)
    );

    geometry.computeVertexNormals(); // clean lighting
    geometry.computeBoundingSphere();
    return geometry;
  }

  createStarField({
    scene,
    count = 4000, 
    innerRadius = 120,
    outerRadius = 150,
    center = new THREE.Vector3(21, 11, 23.15)
  }) {
    const geometry = this.createPointyStarGeometry();

    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      // depthTest: false
        fog: false
    });

    // ⭐ Instanced stars
    const stars = new THREE.InstancedMesh(geometry, material, count);
    stars.frustumCulled = false; // 🔥 CRITICAL
    stars.renderOrder = -1;

    const dummy = new THREE.Object3D();
    const starCenter = new THREE.Vector3(21, 11, 23.15); // city center / camera height

     for (let i = 0; i < count; i++) {
    // --- angles ---
    const theta = Math.random() * Math.PI * 2;

    // top hemisphere only
    const cosPhi = Math.random(); // [0..1]
    const phi = Math.acos(cosPhi);

    // --- radius shell (uniform volume) ---
    const t = Math.random();
    const r = THREE.MathUtils.lerp(innerRadius, outerRadius, Math.cbrt(t));

    // --- spherical → Cartesian ---
    const x = r * Math.sin(phi) * Math.cos(theta);
    const y = r * Math.cos(phi);
    const z = r * Math.sin(phi) * Math.sin(theta);

    dummy.position.set(x, y, z);

    // subtle random orientation
    dummy.rotation.set(0, 0, Math.random() * Math.PI);

    // size (tune freely)
    const size = THREE.MathUtils.randFloat(0.5,0.8);
    dummy.scale.set(size, size, size);

    dummy.updateMatrix();
    stars.setMatrixAt(i, dummy.matrix);
  }

    stars.instanceMatrix.needsUpdate = true;
    scene.add(stars);
    this.loadingManager.increment('Virtualizing galaxy...');
    return stars;
  }


  async init() {
    // Set total assets to load
    this.loadingManager.setTotal(6)
    
    // Load assets
    await this.loadAssets()
    // this.loadingManager.complete()
    
    // Create scene content
    this.createScene()
    
    // axis helper
    const axesHelper = new THREE.AxesHelper( 5 );
    this.sceneManager.scene.add( axesHelper );

    // Start animation loop
    this.animate()
  }

  async loadAssets() {

    this.assetLoader.loadGLTF('/model/macroF.glb', (gltf) => {
      gltf.scene.children.forEach((child) => {

        if (child.isMesh) {
          child.receiveShadow = true;
          if(child.name.includes('panel')){
            if(child.name.includes('sky') ){
              const color = false;

              const tex = new THREE.TextureLoader().load(`./texture/img/BB2.jpeg`);
              tex.minFilter = THREE.LinearFilter;
              tex.magFilter = THREE.LinearFilter;
              tex.generateMipmaps = false;

              const video = document.createElement('video');
              video.src = './texture/img/E2.mp4';   // your file
              video.loop = true;
              video.muted = true;
              video.playsInline = true;
              video.autoplay = true;
              video.crossOrigin = 'anonymous';
              video.load();
              video.play();

              // 2. Create a VideoTexture
              const tex3 = new THREE.VideoTexture(video);

              tex3.minFilter = THREE.LinearFilter;
              tex3.magFilter = THREE.LinearFilter;
              tex3.generateMipmaps = false;

              child.material = new THREE.ShaderMaterial({
                      side: THREE.FrontSide,      // helps if plane is facing away
                      transparent: true,          // alpha is 1 anyway
                      // blending: THREE.AdditiveBlending, // optional CRT glow
                      uniforms: {
                          mapA:         { value: tex },
                          mapB:         { value: tex3},
                          time:        { value: 0 },
                          resolution:  { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
                          stripeWidth: { value: 10.0 },  // tweak 2–6
                          dotHeight:   { value: 6.0 },  // tweak 2–6
                          glowColor:   { value: color? new THREE.Color(0xcc33ff): new THREE.Color(0xFF0400) },
                          uvRotation: { value: Math.PI * 1.5 },
                          uBloomIntensity: { value: 3.0 },
                      },
                      vertexShader: crtPosterShaderMoving.vertexShader,
                      fragmentShader: crtPosterShaderMoving.fragmentShader
              })

            }else if(child.name.includes('mid_red')){
              const color = true;

              const tex = new THREE.TextureLoader().load(`./texture/img/MP.jpeg`);
              tex.minFilter = THREE.LinearFilter;
              tex.magFilter = THREE.LinearFilter;
              tex.generateMipmaps = false;

              child.material = new THREE.ShaderMaterial({
                      side: THREE.FrontSide,      // helps if plane is facing away
                      transparent: true,          // alpha is 1 anyway
                      // blending: THREE.AdditiveBlending, // optional CRT glow
                      uniforms: {
                          map:         { value: tex },
                          time:        { value: 0 },
                          resolution:  { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
                          stripeWidth: { value: 10.0 },  // tweak 2–6
                          dotHeight:   { value: 6.0 },  // tweak 2–6
                          glowColor:   { value: color? new THREE.Color(0xcc33ff): new THREE.Color(0xFF0400) },
                          uvRotation: { value: Math.PI * 2. },
                          uBloomIntensity: { value: 0.5 },
                      },
                      vertexShader: crtPosterShaderStable.vertexShader,
                      fragmentShader: crtPosterShaderStable.fragmentShader
              })
            }else if(child.name.includes('mid_eye')){
              const color = true;

              const tex = new THREE.TextureLoader().load(`./texture/img/RP.jpeg`);
              tex.minFilter = THREE.LinearFilter;
              tex.magFilter = THREE.LinearFilter;
              tex.generateMipmaps = false;

              child.material = new THREE.ShaderMaterial({
                      side: THREE.FrontSide,      // helps if plane is facing away
                      transparent: true,          // alpha is 1 anyway
                      // blending: THREE.AdditiveBlending, // optional CRT glow
                      uniforms: {
                          map:         { value: tex },
                          time:        { value: 0 },
                          resolution:  { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
                          stripeWidth: { value: 10.0 },  // tweak 2–6
                          dotHeight:   { value: 6.0 },  // tweak 2–6
                          glowColor:   { value: color? new THREE.Color(0xcc33ff): new THREE.Color(0xFF0400) },
                          uvRotation: { value: Math.PI * 1. },
                          uBloomIntensity: { value: 0.5 },
                      },
                      vertexShader: crtPosterShaderStable.vertexShader,
                      fragmentShader: crtPosterShaderStable.fragmentShaderNew
              })
            }
            else if(child.name.includes('mid_left')){
              const color = false;

              const tex = new THREE.TextureLoader().load(`./texture/img/CP.jpeg`);
              tex.minFilter = THREE.LinearFilter;
              tex.magFilter = THREE.LinearFilter;
              tex.generateMipmaps = false;

              child.material = new THREE.ShaderMaterial({
                      side: THREE.FrontSide,      // helps if plane is facing away
                      transparent: false,          // alpha is 1 anyway
                      // blending: THREE.AdditiveBlending, // optional CRT glow
                      uniforms: {
                          map:         { value: tex },
                          time:        { value: 0 },
                          resolution:  { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
                          stripeWidth: { value: 10.0 },  // tweak 2–6
                          dotHeight:   { value: 6.0 },  // tweak 2–6
                          glowColor:   { value: color? new THREE.Color(0xcc33ff): new THREE.Color(0xFF0400) },
                          uvRotation: { value: Math.PI * 0. },
                          uBloomIntensity: { value: 1 },
                      },
                      vertexShader: crtPosterShaderStable.vertexShader,
                      fragmentShader: crtPosterShaderStable.fragmentShader
              })
            }
            else if( child.name.includes('panel_right') ){
              const color = true;

              const tex = new THREE.TextureLoader().load(`./texture/img/P.jpeg`);
              tex.minFilter = THREE.LinearFilter;
              tex.magFilter = THREE.LinearFilter;
              tex.generateMipmaps = false;

              child.material = new THREE.ShaderMaterial({
                      side: THREE.FrontSide,      // helps if plane is facing away
                      transparent: false,          // alpha is 1 anyway
                      // blending: THREE.AdditiveBlending, // optional CRT glow
                      uniforms: {
                          map:         { value: tex },
                          time:        { value: 0 },
                          resolution:  { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
                          stripeWidth: { value: 10.0 },  // tweak 2–6
                          dotHeight:   { value: 6.0 },  // tweak 2–6
                          glowColor:   { value: color? new THREE.Color(0xcc33ff): new THREE.Color(0xFF0400) },
                          uvRotation: { value: Math.PI * 2 },
                          uBloomIntensity: { value: 2 },
                          tintMix: { value: 0.2 },
                      },
                      vertexShader: crtPosterShaderStable.vertexShader,
                      fragmentShader: crtPosterShaderStable.fragmentShaderNew2
              })
            }
            else if( child.name.includes('curv_right_red') ){
              const color = true;

              const video = document.createElement('video');
              video.src = './texture/img/R2.mp4';   // your file
              video.loop = true;
              video.muted = true;
              video.playsInline = true;
              video.autoplay = true;
              video.crossOrigin = 'anonymous';
              video.load();
              video.play();

              // 2. Create a VideoTexture
              const tex = new THREE.VideoTexture(video);

              tex.minFilter = THREE.LinearFilter;
              tex.magFilter = THREE.LinearFilter;
              tex.generateMipmaps = false;

              child.material = new THREE.ShaderMaterial({
                      side: THREE.FrontSide,      // helps if plane is facing away
                      transparent: false,          // alpha is 1 anyway
                      // blending: THREE.AdditiveBlending, // optional CRT glow
                      uniforms: {
                          map:         { value: tex },
                          time:        { value: 0 },
                          resolution:  { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
                          stripeWidth: { value: 10.0 },  // tweak 2–6
                          dotHeight:   { value: 6.0 },  // tweak 2–6
                          glowColor:   { value: color? new THREE.Color(0xcc33ff): new THREE.Color(0xFF0400) },
                          uvRotation: { value: Math.PI * 1 },
                          uBloomIntensity: { value: 2.5 },
                      },
                      vertexShader: crtPosterShaderStable.vertexShader,
                      fragmentShader: crtPosterShaderStable.fragmentShader
              })
            }
            
            this.planeMaterial.push(child.material);
          }
        }
        if(child.isLight) {
          child.castShadow = true
          child.intensity *= 1/200000000;
        }
        if(child.name.includes('kaneda_bike')){
          // console.log('found bike:' , child)
          child.children.forEach((bikePart)=>{
            if(bikePart.isMesh){
                bikePart.castShadow = false;
                bikePart.receiveShadow = false;  
                bikePart.material = new THREE.ShaderMaterial({
                  transparent: true,
                  depthWrite: false,
                  // blending: THREE.AdditiveBlending,
                  uniforms: {
                      time: { value: 0 },
                      color: { value: new THREE.Color(0x00ffff) }, // cyan hologram
                      tronColor: { value: new THREE.Color(0x00ffff) }, // cyan
                      opacity: { value: 0.8 },
                      baseColor: { value: new THREE.Color(0x05080f) }, // near-black body
                      glowColor: { value: new THREE.Color(0xff0000) }, // TRON cyan
                      glowStrength: { value: 2.0 }
                  },
                  vertexShader: hollowgramShader.vertexShader,
                  fragmentShader: hollowgramShader.fragmentShaderNew2
              });
              this.planeMaterial.push(bikePart.material);
            }
          })
        }
      })
      this.sceneManager.scene.add(gltf.scene)
      this.loadingManager.increment('Raising megastructures and chrome towers...')
    })

    this.assetLoader.loadGLTF('/model/koifish.glb', (gltf) => {
      let fishMesh = null;
      gltf.scene.traverse((child) => {
        if (child.isMesh && child.geometry) {
          fishMesh = child;
        }
      });
      const baseMap = fishMesh.material.map;
      if (!baseMap) {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, 1, 1);
        baseMap = new THREE.CanvasTexture(canvas);
      }

      fishMesh.material = new THREE.ShaderMaterial({
        instancing: true,   
        transparent: true,
        depthWrite: false,
        depthTest: true,
        // blending: THREE.AdditiveBlending,
        uniforms: {
          time: { value: 0 },
          map:   { value: baseMap },
          color: { value: new THREE.Color(0xffee55) },
          opacity: { value: 0.8 },
          swimSpeed:    { value: 6.0 },   // faster = more energetic
          swimStrength: { value: 0.5 },  // tail amplitude
          bodyLength:   { value: 8.0 },    // match your model size
          turn:         { value: 1.0  },
          turnStrength: { value: 0.5  },
          holoColor: { value: new THREE.Color(0xffee55) }, // cyan / yellow / magenta
          holoIntensity: { value: 1.2 },
          intensity: { value: .0 },
          scale: { value: 0.5 }
        },
        vertexShader: hollowgramShader.vertexShaderNew,
        fragmentShader: hollowgramShader.fragTestFish
      });

      this.planeMaterial.push(fishMesh.material);
      this.initFish(4,fishMesh, fishMesh.position.clone());

      this.loadingManager.increment('Booting aqua-holos...')
    })

    this.assetLoader.loadGLTF('/model/minTest.glb', (gltf) => {

      gltf.scene.children.forEach((child) => {
        if (child.isMesh) {
          this.buildings.push(child)
        } 
      })
      gltf.scene.visible = false;
      this.makeBackgroundInstances(this.buildings);
      this.loadingManager.increment('Syncing hypercities...')
    })

    this.starField = this.createStarField({
      scene: this.sceneManager.scene,
      count: 500,
    });

    this.cars = new Cars(this.sceneManager.scene,this.carPaths, 50,this.loadingManager);
        
    this.loadingManager.increment('Boot cycle complete.')
  }



  createScene() {
    
    // Example: Add a plane
    // const planeGeometry = new THREE.PlaneGeometry(4,8)

    
    // const crtMaterial = this.planeManager.addMovingCRTMat();

    // // Keep reference to update time in animate()
    // this.billboardMaterial = crtMaterial;

    // const plane = new THREE.Mesh(planeGeometry, this.billboardMaterial)
    // plane.name = 'PosterPlane' // friendly name for GUI
    // plane.position.set(42, 12, 21.5)
    // plane.rotation.z = Math.PI * 0.5
    // plane.rotation.y = -Math.PI * 0.5
    // this.sceneManager.scene.add(plane)

    // this.planeManager.addPlane(plane,2,3,true,false);

    // expose plane to the auto GUI so position (and other props) are tweakable
    // SceneManager creates this.sceneManager.gui in setupHelpers()
    // if (this.sceneManager && this.sceneManager.gui) {
    //   AutoGui.addObject(this.sceneManager.gui, plane, 'PosterPlane')
    // }
  }

  animate() {
    if (this.sceneManager.stats) {
      this.sceneManager.stats.begin()
    }

    const delta = this.sceneManager.update()

    // update billboard time uniform
    if (this.billboardMaterial && this.billboardMaterial.uniforms) {
      this.billboardMaterial.uniforms.time.value += delta
      // keep resolution updated
      this.billboardMaterial.uniforms.resolution.value.set(window.innerWidth/2, window.innerHeight/2)
    }

    if(this.cars) this.cars.update(delta);
    this.updateFish(delta);
    this.planeMaterial.forEach((mat)=>{
      if (mat && mat.uniforms) {
        mat.uniforms.time.value += delta
      }
    })

    // Update camera controller
    if (this.cameraController) {
      this.cameraController.update(delta)
    }
    
    // render
    this.sceneManager.render()

    if (this.sceneManager.stats) {
      this.sceneManager.stats.end()
    }

    window.requestAnimationFrame(() => this.animate())
  }
}

//=============================================================================
// START APPLICATION
//=============================================================================

new App()
